import React from 'react';
import {
  ApplianceShell,
  HorizontalHandle,
  HorizontalSplit,
  ToeKick,
  TOE_KICK_HEIGHT_M,
} from './ApplianceFrame';

/**
 * Dishwasher variants:
 *   - exposed-front (default): visible control strip at the top of the
 *     panel + horizontal pull handle below it. Stainless or printed
 *     control face.
 *   - panel-ready: flat panel that matches surrounding cabinetry. No
 *     visible controls; just a toe-kick and (optionally) a recessed
 *     finger-pull at the very top. The plan calls this out explicitly.
 *
 * Field: `obj.panelReady === true` selects the panel-ready variant.
 * The kitchen pipeline does not currently set this field, so the
 * default is exposed-front; downstream styling UI can flip it.
 */

const CONTROL_STRIP_RATIO = 0.08;

export function resolveDishwasherVariant(obj = {}) {
  if (obj.panelReady === true) return 'panel-ready';
  if (obj.subtype === 'panel-ready') return 'panel-ready';
  return 'exposed-front';
}

export function DishwasherSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters,
  obj = {},
  strokeColor,
  strokeWidth,
  view = 'front',
}) {
  if (view === 'left' || view === 'right') {
    return (
      <DishwasherSideView
        view={view}
        depthMeters={depthMeters}
        heightMeters={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
  }
  const variant = resolveDishwasherVariant(obj);
  return (
    <ApplianceShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      {variant === 'exposed-front' ? (
        <ExposedFront
          width={width}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      ) : (
        <PanelReady
          width={width}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      )}
      <ToeKick
        width={width}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </ApplianceShell>
  );
}

DishwasherSchematic.supportsSideViews = true;

/**
 * Side elevation of a dishwasher: rectangle (depth × height) with a
 * slight toe-kick inset at the bottom front and a pull-handle bar
 * projecting from the FRONT edge near the top.
 *
 * Convention: LEFT view places the front face at x = depthMeters
 * (right edge of the drawing). Mirrored for RIGHT.
 */
function DishwasherSideView({
  depthMeters,
  heightMeters,
  strokeColor,
  strokeWidth,
  view,
}) {
  const w = depthMeters;
  const h = heightMeters;
  const toeY = h - TOE_KICK_HEIGHT_M;
  // Toe-kick recess: pulls back from the front edge by a small amount.
  const toeRecess = Math.min(w * 0.15, 0.025);
  const handleProtrusion = Math.min(w * 0.05, 0.02);
  const handleY = h * 0.06;
  const handleLen = Math.min(h * 0.018, 0.025);

  const sideChildren = (
    <>
      {/* Toe-kick: horizontal recess line at top of kick + inset
          vertical at the front edge. */}
      {h > TOE_KICK_HEIGHT_M && (
        <>
          <line
            x1={0}
            y1={toeY}
            x2={w - toeRecess}
            y2={toeY}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            vectorEffect="non-scaling-stroke"
          />
          <line
            x1={w - toeRecess}
            y1={toeY}
            x2={w - toeRecess}
            y2={h}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            vectorEffect="non-scaling-stroke"
          />
        </>
      )}
      {/* Pull-handle bar at the top front */}
      <line
        x1={w}
        y1={handleY - handleLen / 2}
        x2={w + handleProtrusion}
        y2={handleY - handleLen / 2}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={w + handleProtrusion}
        y1={handleY - handleLen / 2}
        x2={w + handleProtrusion}
        y2={handleY + handleLen / 2}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={w + handleProtrusion}
        y1={handleY + handleLen / 2}
        x2={w}
        y2={handleY + handleLen / 2}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
    </>
  );

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
    >
      <rect
        x={0}
        y={0}
        width={w}
        height={h}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {view === 'right' ? (
        <g transform={`translate(${w}, 0) scale(-1, 1)`}>{sideChildren}</g>
      ) : (
        sideChildren
      )}
    </svg>
  );
}

function ExposedFront({ width, height, strokeColor, strokeWidth }) {
  const stripH = height * CONTROL_STRIP_RATIO;
  // Horizontal pull just under the control strip.
  const handleY = stripH + height * 0.04;
  return (
    <g>
      <HorizontalSplit
        width={width}
        y={stripH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <HorizontalHandle
        cx={width / 2}
        y={handleY}
        length={width * 0.8}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

/**
 * Panel-ready: flat cabinet-matched panel + toe-kick. Single thin
 * line at the very top represents the recessed finger-pull — common
 * on integrated DWs (Miele, Bosch 800-series). No control strip.
 */
function PanelReady({ width, height, strokeColor, strokeWidth }) {
  const handleY = Math.min(0.04, (height - TOE_KICK_HEIGHT_M) * 0.05);
  return (
    <HorizontalHandle
      cx={width / 2}
      y={handleY}
      length={width * 0.6}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
}
